import { useCallback, useEffect, useState } from 'react';
import { PERMISSIONS, type Member, type Permission } from '@gc/api-client';

/**
 * Permission management for an enterprise admin. The point of #10 is that a client's own
 * manager composes permission sets for their staff without involving GadgetCloud, so this is
 * a grid of people against capabilities, not a dropdown of fixed roles.
 */

export interface MembersApi {
  listMembers: () => Promise<{ items: Member[] }>;
  setPermission: (membershipId: string, permission: Permission, granted: boolean) => Promise<unknown>;
}

const LABELS: Record<Permission, string> = {
  'gadgets:view': 'View gadgets',
  'gadgets:edit': 'Edit gadgets',
  'repairs:log': 'Log repairs',
  'users:manage': 'Manage users',
  'data:export': 'Export data',
  'audit:view': 'View audit history',
};

type Load = { kind: 'loading' } | { kind: 'ready'; members: Member[] } | { kind: 'error'; message: string };

export function Members({ api }: { api: MembersApi }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [saving, setSaving] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.listMembers()
      .then((r) => live && setLoad({ kind: 'ready', members: r.items }))
      .catch((e: unknown) => live && setLoad({ kind: 'error', message: message(e) }));
    return () => { live = false; };
  }, [api]);

  const toggle = useCallback(async (member: Member, permission: Permission, granted: boolean) => {
    const key = `${member.id}:${permission}`;
    setSaving(key);
    setFailure(null);

    // Optimistic, then reverted on failure — a permission grid that lags every click is
    // unusable, but showing a permission as granted when it was refused would be worse.
    const apply = (on: boolean) => setLoad((prev) => prev.kind !== 'ready' ? prev : {
      kind: 'ready',
      members: prev.members.map((m) => m.id !== member.id ? m : {
        ...m,
        permissions: on
          ? [...(m.permissions ?? []), permission]
          : (m.permissions ?? []).filter((p) => p !== permission),
      }),
    });

    apply(granted);
    try {
      await api.setPermission(member.id, permission, granted);
    } catch (e) {
      apply(!granted);
      setFailure(`Could not ${granted ? 'grant' : 'revoke'} ${LABELS[permission]} for ${member.email}: ${message(e)}`);
    } finally {
      setSaving(null);
    }
  }, [api]);

  if (load.kind === 'loading') return <p>Loading people…</p>;
  if (load.kind === 'error') return <p data-testid="members-error">Could not load people: {load.message}</p>;

  return (
    <section>
      <h2>People</h2>
      <p>Tick a capability to grant it. Changes take effect immediately and are recorded in the audit history.</p>

      {failure && <p role="alert" data-testid="members-failure">{failure}</p>}

      <div className="scroll-x"><table>
        <caption>Members of your organisation and the capabilities each holds</caption>
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Role</th>
            {PERMISSIONS.map((p) => <th scope="col" key={p}>{LABELS[p]}</th>)}
          </tr>
        </thead>
        <tbody>
          {load.members.map((m) => (
            <tr key={m.id} data-testid={`member-${m.email}`}>
              <th scope="row">{m.email}</th>
              <td>{m.role.replace('_', ' ')}</td>
              {PERMISSIONS.map((p) => {
                const held = (m.permissions ?? []).includes(p);
                const key = `${m.id}:${p}`;
                return (
                  <td key={p}>
                    {/* The label is the hit target. A bare 16px checkbox is under the 24px
                        WCAG 2.2 asks for, and these are the most consequential controls in
                        the product — granting someone permission should not need a steady
                        hand on a phone. */}
                    <label className="tick">
                      <input
                        type="checkbox"
                        checked={held}
                        disabled={saving === key}
                        aria-label={`${LABELS[p]} for ${m.email}`}
                        onChange={(e) => void toggle(m, p, e.target.checked)}
                      />
                    </label>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table></div>

      {load.members.length === 0 && <p>Nobody else has joined your organisation yet.</p>}
    </section>
  );
}

const message = (e: unknown): string => (e instanceof Error ? e.message : 'unknown error');
